import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React, { useCallback, useEffect, useState } from 'react';
import { useLoadingDialog } from '../../hooks/backdrop';
import { useFeedback } from '../../hooks/feedback';
import { AssigneeUseTermDTO } from '../../models/dto/AssigneeUseTermDTO';
import { getCancelTokenSource, isCancel } from '../../services/api';
import {
  checkTermToSign,
  confirmUseTermReading,
  findAssigneeUseTerm,
} from '../../services/personnelDepartment.service';
import {
  isAuthenticatedFromGDP,
  SESSION_STORAGE_USER_ID,
} from '../../store/actions/authActions';
import { SESSION_STORAGE_REFERENCE } from '../../store/actions/condominiumActions';

const UseTermsDialog: React.FC = () => {
  const [confirmReading, setConfirmReading] = useState(false);
  const [hasToSign, setHasToSign] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enableConfirmReading, setEnableConfirmReading] = useState(false);
  const [useTerm, setUseTerm] = useState<AssigneeUseTermDTO>();
  const descriptionElementRef = React.useRef<HTMLElement>(null);
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();

  const handleTextScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const bottom =
      event.currentTarget.scrollHeight - event.currentTarget.scrollTop ===
      event.currentTarget.clientHeight;
    if (bottom) {
      setEnableConfirmReading(bottom);
    }
  };

  const submitUseTermReading = useCallback(async () => {
    try {
      const userId = sessionStorage.getItem(SESSION_STORAGE_USER_ID);
      if (useTerm && confirmReading && userId) {
        handleLoadingDialog(true);
        await confirmUseTermReading(String(useTerm?.id), userId);
        addFeedback({
          type: 'success',
          description: 'Termo assinado com sucesso.',
        });
        setHasToSign(false);
      }
    } catch (error) {
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao assinar o termo de uso.',
      });
    } finally {
      handleLoadingDialog(false);
    }
  }, [addFeedback, confirmReading, handleLoadingDialog, useTerm]);

  useEffect(() => {
    const cancelTokenSource = getCancelTokenSource();

    const findAllOccurrences = async () => {
      try {
        setLoading(true);
        const reference = sessionStorage.getItem(SESSION_STORAGE_REFERENCE);
        if (isAuthenticatedFromGDP() && reference) {
          const { data } = await checkTermToSign(Number(reference));
          const thereIsTermToSign = data && data.termo && !data.assinado;
          setHasToSign(thereIsTermToSign);

          if (thereIsTermToSign) {
            const { data: term } = await findAssigneeUseTerm();
            setUseTerm(term);
          }
        }
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
          addFeedback({
            type: 'error',
            description: 'Não foi possível carregar o termo.',
          });
        }
      } finally {
        setLoading(false);
      }
    };

    findAllOccurrences();
    return () => {
      cancelTokenSource.cancel();
    };
  }, [addFeedback]);

  useEffect(() => {
    if (hasToSign) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [hasToSign]);

  return (
    <Dialog
      open={hasToSign}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      <DialogContent dividers onScroll={handleTextScroll}>
        <DialogTitle id="scroll-dialog-title" style={{ padding: '16px 0px' }}>
          Termos de uso do Tablet
        </DialogTitle>

        {loading ? (
          <Skeleton animation="wave" height={30} width="100%" />
        ) : (
          <DialogContentText
            id="scroll-dialog-description"
            ref={descriptionElementRef}
            tabIndex={-1}
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {useTerm?.conteudo ?? ''}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={confirmReading}
              onChange={event => setConfirmReading(event.target.checked)}
              disabled={!enableConfirmReading}
            />
          }
          color="secondary"
          label="Declaro que li e estou ciente."
        />
        <Button
          variant="contained"
          color="primary"
          disabled={!confirmReading || loading}
          onClick={() => submitUseTermReading()}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UseTermsDialog;
